import { useQuery } from "@tanstack/react-query";
import { SHELL_STALE_MS } from "../../api/query-client";
import { useApi } from "../../api/context";
import type { EntitlementState } from "../../api/types";
import { MARKETPLACE_SUBMIT_FEATURE } from "../workspace/nav-config";

/** Feature keys whose nav entries are hidden (not disabled) when the plan lacks them. */
export const GATED_NAV_FEATURES = [
  "flows.builder",
  "catalog.manager",
  "commerce.connectors",
  "ai.agent",
  MARKETPLACE_SUBMIT_FEATURE,
] as const;

/** Cached for the app lifetime key: one `GET /v1/workspaces/:id/entitlements` per workspace. */
export function useEntitlements(workspaceId: string): Record<string, boolean> | undefined {
  const api = useApi();
  const query = useQuery({
    queryKey: ["entitlements", workspaceId],
    queryFn: () => api.getEntitlements(workspaceId),
    staleTime: SHELL_STALE_MS,
  });
  return query.data;
}

/**
 * WP-316: the API is the only place the resolution order lives — this hook never re-derives it.
 * `null` while loading or on failure (callers treat "unknown" as allowed; the server guard is
 * the boundary). Without `current` it reads from the shared batched map (UX-15); a numeric
 * `current` needs the limit check, so it uses the per-key endpoint (cached by key).
 */
export function useEntitlement(
  workspaceId: string,
  featureKey: string,
  current?: number,
): EntitlementState | null {
  const api = useApi();
  const all = useEntitlements(workspaceId);
  const limited = useQuery({
    queryKey: ["entitlement", workspaceId, featureKey, current],
    queryFn: () => api.getEntitlement(workspaceId, featureKey, current),
    enabled: current !== undefined,
    staleTime: SHELL_STALE_MS,
  });
  if (current !== undefined) return limited.data ?? null;
  const enabled = all?.[featureKey];
  return enabled === undefined ? null : { featureKey, enabled };
}

/** `{ [featureKey]: enabled }` for the nav-gated features; a missing key means unknown. */
export function useGatedFeatures(workspaceId: string): Record<string, boolean> {
  const all = useEntitlements(workspaceId);
  if (!all) return {};
  return Object.fromEntries(
    GATED_NAV_FEATURES.filter((k) => all[k] !== undefined).map((k) => [k, all[k] as boolean]),
  );
}
